import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {ArrowRight, MapPin, Award, Clock} from 'lucide-react'
import { HomeSection } from '@/hooks/useHomeSections'
import CandidateShield from '@/components/CandidateShield'
import {
  useAvailableCandidates,
  formatProfileLabel,
  formatRemaining,
  formatOnline,
  type AvailableCandidate,
  type CandidateQualification,
} from '@/hooks/useAvailableCandidates'

const DISPLAY_FONT = "'Bricolage Grotesque', Inter, system-ui, sans-serif"

/** Clé spéciale : section "ILS SONT DISPONIBLES IMMÉDIATEMENT !" sur la home. */
const AVAILABILITY_SECTION_KEY = 'section-1779624063612'

/** Qualifications "univers bleu" (MNS-style) acceptées comme 2ᵉ profil. */
const BLUE_QUALIFICATIONS: CandidateQualification[] = ['BPJEPS AAN', 'BEESAN', 'STAPS']

interface Props {
  section: HomeSection
}

/**
 * Met le mot "Immédiatement" en surbrillance dégradée
 * (rouge → fuchsia → bleu), en respectant l'accent et la ponctuation.
 */
function renderTitleWithGradient(title: string) {
  // Capture "Immédiatement" (ou "Immediatement") + d'éventuels "!" qui suivent
  // (avec espaces/insécables intermédiaires), pour inclure le "!" dans le dégradé.
  const regex = /(Imm[ée]diatement[\s\u00A0]*!*)/i
  const parts = title.split(regex)
  return parts.map((part, i) => {
    if (regex.test(part)) {
      return (
        <span
          key={i}
          className="bg-gradient-to-r from-red-400 via-fuchsia-400 to-blue-400 bg-clip-text text-transparent"
        >
          {part}
        </span>
      )
    }
    return <span key={i}>{part}</span>
  })
}

/**
 * Couleurs accent par qualification pour les mini-cartes profil.
 * Charte respectée :
 *  - BNSSA → rouge
 *  - MNS / BPJEPS AAN / BEESAN / STAPS → univers bleu (nuances différentes)
 */
const QUAL_THEME: Record<
  CandidateQualification,
  { bar: string; chip: string }
> = {
  BNSSA: {
    bar: 'bg-gradient-to-r from-red-500 via-red-600 to-rose-600',
    chip: 'bg-red-50 text-red-700 border-red-200',
  },
  MNS: {
    bar: 'bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700',
    chip: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  'BPJEPS AAN': {
    bar: 'bg-gradient-to-r from-indigo-500 via-indigo-600 to-blue-700',
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  },
  BEESAN: {
    bar: 'bg-gradient-to-r from-indigo-500 via-indigo-600 to-blue-700',
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  },
  STAPS: {
    bar: 'bg-gradient-to-r from-indigo-500 via-indigo-600 to-blue-700',
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  },
}

function MiniProfileCard({ candidate }: { candidate: AvailableCandidate }) {
  const theme =
    QUAL_THEME[candidate.qualification] ?? {
      bar: 'bg-gradient-to-r from-gray-500 to-gray-700',
      chip: 'bg-gray-100 text-gray-700 border-gray-200',
    }
  const profileLabel = formatProfileLabel(candidate.qualification, candidate.publicId)
  const remaining = candidate.availableUntil ? formatRemaining(candidate.availableUntil) : ''
  const onlineLabel = formatOnline(candidate.availableSince)

  return (
    <Link
      to="/disponibles"
      className="group relative block bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.12] hover:border-white/20 rounded-2xl overflow-hidden backdrop-blur-sm transition-all duration-300 hover:-translate-y-1"
    >
      {/* Accent bar */}
      <div className={`h-1 w-full ${theme.bar}`} aria-hidden="true" />

      <div className="p-5 sm:p-6 flex flex-col gap-4">
        {/* Status banner */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide rounded-full px-3 py-1 bg-white/[0.08] border border-white/[0.18]">
            <span className="relative flex w-2 h-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-white">Disponible immédiatement</span>
          </span>
          <span className="text-[11px] font-medium text-white/60 truncate">{onlineLabel}</span>
        </div>

        {/* Écusson héraldique + ID */}
        <div className="flex items-center gap-4">
          <CandidateShield
            qualification={candidate.qualification}
            size={64}
            variant="dark"
            showLabel
          />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/50">
              Identifiant
            </p>
            <h3 className="font-mono text-base sm:text-lg font-black text-white tracking-tight break-all mt-0.5">
              {profileLabel}
            </h3>
          </div>
        </div>

        {/* Chips qualification + localisation */}
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 text-[11px] font-bold border rounded-full px-2.5 py-1 ${theme.chip}`}
          >
            <Award className="w-3 h-3" />
            {candidate.qualification}
          </span>
          {(candidate.region || candidate.city) && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-white/80 bg-white/[0.08] border border-white/[0.12] rounded-full px-2.5 py-1">
              <MapPin className="w-3 h-3" />
              {candidate.region || candidate.city}
            </span>
          )}
        </div>

        {/* Compte à rebours */}
        {remaining && remaining !== 'Expiré' && (
          <div className="rounded-xl border border-white/[0.12] bg-white/[0.04] px-3 py-2 flex items-center gap-2">
            <Clock className="w-4 h-4 flex-shrink-0 text-white/70" />
            <span className="text-xs font-semibold text-white/85 break-words">{remaining}</span>
          </div>
        )}

        {/* CTA discret */}
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white/90 group-hover:text-white">
          Voir le profil
          <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  )
}

/**
 * Section spéciale "ILS SONT DISPONIBLES IMMÉDIATEMENT !" :
 * - Background gradient identique au hero de /disponibles.
 * - 2 mini-profils (1 BNSSA + 1 BPJEPS AAN/BEESAN/STAPS) entre titre et CTA.
 * - Si 0 ou 1 seul profil disponible → retourne null (section désactivée).
 */
function AvailabilitySection({ section }: { section: HomeSection }) {
  const { items, fetchPublic, loading } = useAvailableCandidates()
  const [hasFetched, setHasFetched] = useState(false)

  useEffect(() => {
    fetchPublic().finally(() => setHasFetched(true))
  }, [fetchPublic])

  // On privilégie la mixité (1 profil BNSSA + 1 profil "bleu"), mais on reste
  // résilient : si une seule catégorie est disponible, on affiche tout de même
  // jusqu'à 2 profils disponibles plutôt que de masquer toute la section.
  const featuredCandidates = useMemo(() => {
    const available = items.filter((c) => c.isAvailableNow !== false)
    const bnssaCand = available.find((c) => c.qualification === 'BNSSA')
    const blueCand = available.find((c) =>
      BLUE_QUALIFICATIONS.includes(c.qualification as CandidateQualification)
    )

    const selected: AvailableCandidate[] = []
    if (bnssaCand) selected.push(bnssaCand)
    if (blueCand) selected.push(blueCand)

    // Compléter jusqu'à 2 cartes avec d'autres profils disponibles si besoin.
    if (selected.length < 2) {
      for (const c of available) {
        if (selected.length >= 2) break
        if (!selected.some((s) => s._id === c._id)) selected.push(c)
      }
    }

    return selected
  }, [items])

  // Tant que la requête n'a pas répondu, on évite le flash : on ne rend rien.
  if (!hasFetched || loading) return null

  // Désactivation uniquement s'il n'y a aucun profil disponible à mettre en avant.
  if (featuredCandidates.length === 0) return null

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#0b1020] via-[#0e1733] to-[#1a1030] text-white">
      {/* Halos rouge/bleu — identiques à /disponibles */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-60 bg-[radial-gradient(ellipse_at_top_left,rgba(220,38,38,0.45),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(37,99,235,0.45),transparent_55%)]"
      />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24">
        <div className="text-center mb-10">
          {section.subtitle && (
            <span className="inline-block text-[11px] font-bold uppercase tracking-[0.14em] mb-3 text-white/60">
              {section.subtitle}
            </span>
          )}
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-[-0.03em] text-white"
            style={{ fontFamily: DISPLAY_FONT }}
          >
            {renderTitleWithGradient(section.title)}
          </h2>
          {section.content && section.content.replace(/<[^>]*>/g, '').trim() && (
            <div
              className="mt-4 max-w-2xl mx-auto text-base leading-relaxed text-white/80 [&_p]:m-0 [&_a]:text-blue-300 [&_a]:underline [&_strong]:text-white"
              dangerouslySetInnerHTML={{ __html: section.content }}
            />
          )}
        </div>

        {/* Mini-cartes profils */}
        <div
          className={`grid grid-cols-1 gap-5 mb-10 mx-auto ${
            featuredCandidates.length > 1 ? 'sm:grid-cols-2 max-w-3xl' : 'max-w-md'
          }`}
        >
          {featuredCandidates.map((candidate) => (
            <MiniProfileCard key={candidate._id} candidate={candidate} />
          ))}
        </div>

        {/* CTA */}
        {section.ctaLabel && section.ctaUrl && (
          <div className="text-center">
            <Link
              to={section.ctaUrl}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-bold transition-colors bg-white text-[#0b1220] hover:bg-gray-100"
            >
              {section.ctaLabel}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * Section "custom" générique de la home (rendu standard pour toutes les autres
 * sections custom configurées dans le backoffice).
 */
function GenericCustomSection({ section }: { section: HomeSection }) {
  const bg = section.background || 'white'

  const containerClass =
    bg === 'dark'
      ? 'bg-[#0b1220] text-white'
      : bg === 'gray'
      ? 'bg-[#f8fafc] text-[#0b1220]'
      : bg === 'gradient'
      ? 'text-white'
      : 'bg-white text-[#0b1220]'

  const containerStyle =
    bg === 'gradient'
      ? {
          background:
            'linear-gradient(135deg,#dc2626 0%,#7f1d1d 50%,#1e3a8a 75%,#2563eb 100%)',
        }
      : undefined

  const titleClass = bg === 'dark' || bg === 'gradient' ? 'text-white' : 'text-[#0b1220]'
  const subtitleClass = bg === 'dark' || bg === 'gradient' ? 'text-gray-300' : 'text-gray-400'
  const proseClass =
    bg === 'dark' || bg === 'gradient'
      ? '[&_p]:text-gray-200 [&_li]:text-gray-200 [&_a]:text-blue-300 [&_strong]:text-white [&_h2]:text-white [&_h3]:text-white'
      : '[&_p]:text-gray-700 [&_li]:text-gray-700 [&_a]:text-blue-600 [&_strong]:text-gray-900 [&_h2]:text-gray-900 [&_h3]:text-gray-900'

  return (
    <section className={`py-20 sm:py-24 ${containerClass}`} style={containerStyle}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          {section.subtitle && (
            <span
              className={`inline-block text-[11px] font-bold uppercase tracking-[0.14em] mb-3 ${subtitleClass}`}
            >
              {section.subtitle}
            </span>
          )}
          <h2
            className={`text-3xl sm:text-4xl lg:text-5xl font-black tracking-[-0.03em] ${titleClass}`}
            style={{ fontFamily: DISPLAY_FONT }}
          >
            {section.title}
          </h2>
        </div>

        <div
          className={`prose-rte max-w-none text-base leading-relaxed [&_p]:mb-4 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:mt-5 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_blockquote]:italic ${proseClass}`}
          dangerouslySetInnerHTML={{ __html: section.content }}
        />

        {section.ctaLabel && section.ctaUrl && (
          <div className="text-center mt-8">
            <Link
              to={section.ctaUrl}
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-colors ${
                bg === 'dark' || bg === 'gradient'
                  ? 'bg-white text-[#0b1220] hover:bg-gray-100'
                  : 'bg-[#0b1220] text-white hover:bg-black'
              }`}
            >
              {section.ctaLabel}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  )
}

export default function HomeCustomSection({ section }: Props) {
  // Routing : la section "Disponibles Immédiatement" a un rendu dédié.
  if (section.sectionKey === AVAILABILITY_SECTION_KEY) {
    return <AvailabilitySection section={section} />
  }
  return <GenericCustomSection section={section} />
}
